import kotlin.math.ceil
import kotlin.math.floor
import kotlin.math.round

/** Guards the `<`/`>` stepping below against Double rounding noise (e.g. 0.1 * 3 != 0.3). */
private const val EPSILON = 1e-9

/** Formats a value for inputs and previews without trailing noise: 2.0 -> "2", 2.499 -> "2.5". */
fun formatNumber(value: Double): String {
    val rounded = round(value * 100) / 100
    return if (rounded == rounded.toLong().toDouble()) rounded.toLong().toString() else rounded.toString()
}

/**
 * One tap of the "<" button next to an exercise. [unit] is that exercise's norm
 * (`points_divisor` -- how many units make one point: 30 for push-ups, 1 for the plank): a value
 * sitting between multiples snaps down to the nearest one, a value already on a multiple drops a
 * whole [unit]. Never goes below zero.
 */
fun stepDown(value: Double, unit: Double = 1.0): Double {
    val step = unit.takeIf { it > 0.0 } ?: 1.0
    val flooredToUnit = floor(value / step + EPSILON) * step
    val result = if (value > flooredToUnit + EPSILON) flooredToUnit else flooredToUnit - step
    return result.coerceAtLeast(0.0)
}

/** Mirror image of [stepDown]: snaps up to the nearest multiple of [unit], or adds a whole one. */
fun stepUp(value: Double, unit: Double = 1.0): Double {
    val step = unit.takeIf { it > 0.0 } ?: 1.0
    val ceiledToUnit = ceil(value / step - EPSILON) * step
    return if (value < ceiledToUnit - EPSILON) ceiledToUnit else ceiledToUnit + step
}

/** Czech day count with the right plural: 1 den, 2-4 dny, 5+ dní. UI text, hence Czech. */
fun czechDays(count: Int): String = when {
    count == 1 -> "1 den"
    count in 2..4 -> "$count dny"
    else -> "$count dní"
}
